import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  User, 
  Settings, 
  LogOut, 
  ChevronDown, 
  UserCircle,
  Zap,
  Crown
} from 'lucide-react'
import { UserLimits } from '@/types'
import { AuthUser } from '@/services/auth'
import { useTranslation } from 'react-i18next'

interface UserDropdownProps {
  user: AuthUser
  userLimits?: UserLimits | null
  onSignOut: () => void
}

export default function UserDropdown({ user, userLimits, onSignOut }: UserDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const displayName = user.username || user.email?.split('@')[0] || t('userDropdown.fallbackUser')
  const remainingPrompts = userLimits ? userLimits.maxPrompts - userLimits.promptCount : 0

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-xl transition-colors group"
      >
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-white" />
          </div>
          <span className="text-white/80 text-sm font-medium max-w-20 truncate">
            {displayName}
          </span>
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-white/60 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-64 bg-slate-800/95 backdrop-blur-md border border-white/20 rounded-xl shadow-xl z-50"
          >
            {/* User Info Header */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">
                    {displayName}
                  </p>
                  <p className="text-white/60 text-sm truncate">
                    {user.email}
                  </p>
                </div>
              </div>
              
              {/* Quick Limit Info */}
              {userLimits && (
                <div className="mt-3 p-2 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white/70">{t('userDropdown.remainingPrompts')}</span>
                    <div className="flex items-center space-x-1">
                      <Zap className="w-3 h-3 text-yellow-400" />
                      <span className={`font-bold ${
                        remainingPrompts > 5 ? 'text-green-400' : 
                        remainingPrompts > 0 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {remainingPrompts}
                      </span>
                      <span className="text-white/40">/ {userLimits.maxPrompts}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Menu Items */}
            <div className="p-2">
              <Link
                to="/profile"
                onClick={() => setIsOpen(false)}
                className="flex items-center space-x-3 w-full px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors group"
              >
                <UserCircle className="w-4 h-4 group-hover:text-violet-400 transition-colors" />
                <span className="text-sm">{t('userDropdown.profile')}</span>
              </Link>

              <Link
                to="/settings"
                onClick={() => setIsOpen(false)}
                className="flex items-center space-x-3 w-full px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors group"
              >
                <Settings className="w-4 h-4 group-hover:text-violet-400 transition-colors" />
                <span className="text-sm">{t('userDropdown.settings')}</span>
              </Link>

              {/* Premium Upgrade (if needed) */}
              {userLimits && remainingPrompts <= 5 && (
                <div className="my-2 px-3">
                  <div className="p-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-lg opacity-60">
                    <div className="flex items-center space-x-2 mb-1">
                      <Crown className="w-3 h-3 text-yellow-400" />
                      <span className="text-yellow-300 text-xs font-medium">{t('userDropdown.premium')}</span>
                    </div>
                    <p className="text-yellow-200/80 text-xs">
                      {t('userDropdown.comingSoon')}
                    </p>
                  </div>
                </div>
              )}

              <div className="border-t border-white/10 mt-2 pt-2">
                <button
                  onClick={() => {
                    setIsOpen(false)
                    onSignOut()
                  }}
                  className="flex items-center space-x-3 w-full px-3 py-2 text-red-300 hover:text-red-200 hover:bg-red-500/10 rounded-lg transition-colors group"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">{t('userDropdown.signOut')}</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
